/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { lazy, Suspense, useEffect } from "react";
import { MapPin } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import ErrorBoundary, { SentryFallback } from "@/components/ErrorBoundary";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PendingInviteProvider } from "@/contexts/PendingInviteContext";
import { WalkMeHomeProvider } from "@/contexts/WalkMeHomeContext";
import { SimpleSOSProvider } from "@/contexts/SimpleSOSContext";
import SimpleSOSOverlay from "@/components/SOS/SimpleSOSOverlay";
import OfflineBanner from "@/components/OfflineBanner";
import FakeCallScreen from "@/components/SafeCircle/FakeCall/FakeCallScreen";
import NotificationToast from "@/components/SafeCircle/Notifications/NotificationToast";
import CircleInvitationModal from "@/components/SafeCircle/Circle/CircleInvitation";
import SOSTrigger from "@/components/SafeCircle/SOS/SOSTrigger";
import { SOSEventProvider } from "@/contexts/SOSEventContext";
import { ActivityProvider } from "@/contexts/ActivityContext";
import EulaScreen from "@/components/Auth/EulaScreen";
import AppPhoneGate from "@/components/onboarding/AppPhoneGate";
import OnboardingGate from "@/components/onboarding/OnboardingGate";
import LoggedOutFlow from "@/components/onboarding/LoggedOutFlow";
import SentryTest from "@/components/dev/SentryTest";
import ForceUpdateModal from "@/components/ForceUpdateModal";
import { useNativePushToken } from "@/hooks/useNativePushToken";
import { useApnsRegistration } from "@/hooks/useApnsRegistration";
// Route pages are lazy-loaded so the initial bundle (first paint / onboarding)
// does NOT pull in the heavy page deps — Index alone drags in
// @react-google-maps (~7MB source), recharts, framer-motion, etc., which are
// only needed AFTER login. Splitting them out shrinks the cold-launch chunk and
// speeds up time-to-first-paint. All routes already render inside <Suspense>
// boundaries below, so the lazy fallback is handled.
const Index = lazy(() => import("./pages/Index.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const TrackingPage = lazy(() => import("./pages/TrackingPage.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const Support = lazy(() => import("./pages/Support.tsx"));
const InviteLanding = lazy(() => import("./pages/InviteLanding.tsx"));
import { useDeepLinks } from "@/hooks/useDeepLinks";

// DEV-ONLY Closr primitives preview. import.meta.env.DEV is statically
// `false` in production, so this ternary's lazy(import()) branch is
// dead-code-eliminated and the preview chunk never ships.
const ClosrPrimitivesPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/_ClosrPrimitivesPreview"))
  : null;

const queryClient = new QueryClient();

// Cold-launch / auth-rehydrate screen. Renders the BRAND LOGO (gradient pin on
// the dark app background) instead of a bare spinner, so it matches the native
// launch splash and the web #boot-splash. The whole launch — OS splash → web
// boot-splash → this bootstrapping screen → app — then reads as one continuous
// dark logo screen that flows into the app, with no separate "loading page".
function FullScreenSpinner() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-16 h-16 rounded-2xl gradient-bg flex items-center justify-center shadow-lg animate-pulse">
        <MapPin className="text-primary-foreground" size={28} />
      </div>
    </div>
  );
}

function AuthErrorScreen({
  message,
  onRetry,
  onSignOut,
}: {
  message: string | null;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-sm text-foreground max-w-xs">
        {message ?? "Something went wrong while signing you in."}
      </p>
      <button
        onClick={onRetry}
        className="w-full max-w-xs gradient-bg text-primary-foreground py-3 rounded-xl text-sm font-semibold"
      >
        Retry
      </button>
      <button
        onClick={onSignOut}
        className="w-full max-w-xs glass-card py-3 rounded-xl text-sm font-medium text-muted-foreground"
      >
        Sign out
      </button>
    </div>
  );
}

export function AppRoutes() {
  const { user, state, error, acceptEula, declineEula, retryEula, logout } =
    useAuth();

  // Android: paint the status bar to match the dark app theme. iOS handles
  // this via Info.plist (UIStatusBarStyle) so no runtime call is needed.
  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return;
    void (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: "#0a0a1a" });
      } catch (err) {
        console.warn("[App] StatusBar config failed:", err);
      }
    })();
  }, []);

  // iOS APNs registration — runs pre-auth (NOT gated by `user`) so Firebase
  // Phone Auth has the device token ready before the first OTP request in the
  // phone-first flow. No-op off iOS. (Bug 1, layer 1.)
  useApnsRegistration();

  // Auth-dependent hooks: push-token registration (Android FCM) and deep-link
  // handler. useDeepLinks must be mounted at the app root so universal-link
  // taps arriving before auth resolves (cold launch from iMessage) are still
  // captured and replayed once the user is signed in.
  useNativePushToken();
  useDeepLinks();

  // Explicit state machine — no `loading && !eulaSigned` boolean soup.
  // `bootstrapping` covers the cold-launch rehydrate so the login screen
  // never flashes; `idle`/`authenticated` fall through to the router.
  switch (state) {
    case "bootstrapping":
    case "checkingProfile":
    case "checkingEula":
      return <FullScreenSpinner />;
    case "eulaRequired":
      return <EulaScreen onAccept={acceptEula} onDecline={declineEula} />;
    case "error":
      return (
        <AuthErrorScreen
          message={error}
          onRetry={() => void retryEula()}
          // Hard: this is the escape hatch from a broken auth state, so the
          // kept session is precisely what the user needs cleared. A soft
          // logout would preserve the session they're trying to get out of.
          onSignOut={() => void logout({ hard: true })}
        />
      );
    default:
      // "authenticating" + "idle" fall through to the router, where the
      // `!user` branch renders LoggedOutFlow (Welcome → phone sign-in).
      // "authenticated" → app routes. All handled below.
      break;
  }

  // Shared route tree — public routes are always reachable; the
  // authenticated branch adds Index / TrackingPage / NotFound. Hoisted to a
  // const so it can be nested inside the SOS providers on the authenticated
  // path without duplicating route definitions. Pre-fix the `<Routes>` lived
  // as a sibling of `<SimpleSOSProvider>`, which left `<Index>` (and its
  // descendant `<SOSButton>`) outside the provider — `useSimpleSOS()` then
  // threw on first render after sign-in.
  // Wrapped in <Suspense> because the route pages are now lazy()-loaded
  // (code-split). The fallback is the same branded logo screen as the launch
  // splash, so a lazy chunk load never flashes a bare/blank screen.
  const routeTree = (
    <Suspense fallback={<FullScreenSpinner />}>
    <Routes>
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/support" element={<Support />} />
      <Route path="/invite/:token" element={<InviteLanding />} />
      {/* PUBLIC: a Walk Me Home tracking link (SMS to an emergency contact)
          must open the live tracker WITHOUT login. Previously /track lived in
          the authed branch below, so a logged-out recipient fell through to
          LoggedOutFlow (the welcome screen) — the reported bug. TrackingPage
          reads walkMeHome/{sessionId} by the unguessable id and uses no auth
          state, so it is safe to serve publicly (the RTDB per-session read is
          public by the same capability-token design). Keep this ABOVE the
          user gate so it resolves for authed and anonymous visitors alike. */}
      <Route path="/track/:sessionId" element={<TrackingPage />} />
      {import.meta.env.DEV && ClosrPrimitivesPreview && (
        <Route
          path="/dev/closr-preview"
          element={
            <Suspense
              fallback={<div className="min-h-screen bg-background" />}
            >
              <ClosrPrimitivesPreview />
            </Suspense>
          }
        />
      )}
      {!user || user.isAnonymous ? (
        // Anonymous sessions exist only to read /inviteTokens for the
        // invite landing. They should never reach Index — force the
        // logged-out onboarding flow for every non-public path so the
        // visitor signs up properly. MyAmbits is phone-only: LoggedOutFlow
        // shows Welcome → phone entry → OTP, and verifyPhone
        // (signInWithCredential) creates the account on first verification.
        <Route path="*" element={<LoggedOutFlow />} />
      ) : (
        <>
          <Route path="/" element={<Index />} />
          {/* /app is the marketing site's "Open App" entry point (home.html
              CTAs → /app; Vercel rewrites it to index.html). It must render the
              app for a logged-in user, same as "/". Without this, an authed
              visitor hitting /app fell through to the "*" catch-all below and
              got the 404 NotFound page — the reported bug. (Logged-out visitors
              were unaffected: their "*" branch is LoggedOutFlow, not NotFound.)
              Tabs (map/friends/etc.) are internal Index state, not URLs, so no
              other paths need routing here. */}
          <Route path="/app" element={<Index />} />
          {/* /track/:sessionId is now served from the PUBLIC block above so it
              works for logged-out SMS recipients too. */}
          <Route path="*" element={<NotFound />} />
        </>
      )}
    </Routes>
    </Suspense>
  );

  return (
    <WalkMeHomeProvider>
      {/* Build 91 P1-8 — instant offline banner. Renders nothing
          when online; flips on synchronously via navigator.onLine
          when the OS drops the connection so the user knows the
          app isn't broken — just disconnected. */}
      <OfflineBanner />
      {user ? (
        <SOSEventProvider>
          <ActivityProvider>
          <SimpleSOSProvider>
            {/* Build 108 — app-wide mandatory phone verification.
                AppPhoneGate sits between SimpleSOSProvider and the
                route tree so SOSTrigger + SimpleSOSOverlay remain
                mounted as siblings — if the gate sheet somehow stalls,
                the SOS hardware path (shake/volume) still reaches the
                user. Once /users/{uid}/phoneNumber lands, the gate
                unmounts itself and the route tree renders normally. */}
            <AppPhoneGate>
              {/* Post-phone, post-EULA onboarding sequencer (profile →
                  emergency → discovery). Nested INSIDE AppPhoneGate so it
                  only runs once a phone is verified; like the phone gate it
                  renders a full-screen step while incomplete, then falls
                  through to the route tree. SOSTrigger / SimpleSOSOverlay
                  stay mounted as siblings below, so the safety hardware path
                  is reachable even mid-onboarding. */}
              <OnboardingGate>
                {routeTree}
                <NotificationToast />
                <CircleInvitationModal />
                <FakeCallScreen />
              </OnboardingGate>
            </AppPhoneGate>
            <SOSTrigger />
            {/* Build 91 P0-14 — full-screen countdown + active SOS
                overlay. Reads state from SimpleSOSContext and renders
                null while idle, so it sits dormant alongside the
                existing SafeCircle SOSTrigger. The two SOS flows don't
                share state — SimpleSOS is the baseline that always
                works (no SafeCircle profile required); the SafeCircle
                elaborated path (StealthMode + circle fan-out) remains
                reachable via shake/volume/deviation triggers. */}
            <SimpleSOSOverlay />
          </SimpleSOSProvider>
          </ActivityProvider>
        </SOSEventProvider>
      ) : (
        routeTree
      )}
      <SentryTest />
      {/* Launch-time build-version gate. Mounted outside the auth branches so
          it gates every user (incl. logged-out) on an unsupported build.
          Renders null unless the installed build is below minSupportedBuild;
          fail-open while the config resolves, so it never blocks render. */}
      <ForceUpdateModal />
    </WalkMeHomeProvider>
  );
}

const App = () => {
  // DEV-ONLY: a direct load of /dev/closr-preview short-circuits the entire
  // provider/auth/router shell — including the fixed-position SentryTest
  // ("Dev: Error Monitoring") overlay — so the primitives preview can be
  // eyeballed on-device without fighting the dev overlay or the auth gate.
  // Production: import.meta.env.DEV is statically false, so this whole
  // branch and the lazy import are dead-code-eliminated.
  if (
    import.meta.env.DEV &&
    ClosrPrimitivesPreview &&
    typeof window !== "undefined" &&
    window.location.pathname === "/dev/closr-preview"
  ) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <ClosrPrimitivesPreview />
      </Suspense>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AuthProvider>
              <PendingInviteProvider>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </PendingInviteProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
};

export default App;
